import * as React from 'react';
import { Box, Button, Link, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../../Components/DashboardTheme/DashboardTheme';
import { BodyText } from '../../Components/Typography/Typography';
import Activities from '../Activities/Activities';
import RingSide from '../RingSide/RingSide';
import SponsorPage from '../SponsorPage/SponsorPage';
import Footer from '../Footer/Footer';
import ChiSiamo from '../ChiSiamo/ChiSiamo';
import OneChampionship from '../OneChampionship/OneChampionship';
import heroImg from '../../img/mario-home-2.webp';
import c4uLogo from '../../img/sponsor-img/c4u-logo-registrato.svg';

export default function HomePage() {
    const theme = useTheme();
    const isScreenSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const scrollToChiSiamo = () => {
        document.getElementById('chiSiamoSection')?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToContatti = () => {
        document.getElementById('contattiSection')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <Box sx={{ backgroundColor: tokens.ink }}>
            {/* ==========================================================
                HERO
            ========================================================== */}
            <Box
                component="section"
                sx={{
                    position: 'relative',
                    height: { xs: '100svh', md: '100vh' },
                    minHeight: 560,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                {/* Background */}
                <Box
                    component="img"
                    src={heroImg}
                    alt="Allenamento di Muay Thai alla Vivenzio Gym"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center 20%',
                        filter: 'grayscale(30%) contrast(1.1) brightness(0.5)',
                        animation: 'vgSlowZoom 22s ease-out forwards',
                    }}
                />

                {/* Gradient scrims: bottom for the copy, red wash on the left */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(180deg, ${tokens.ink}CC 0%, transparent 32%, ${tokens.ink}E6 88%, ${tokens.ink} 100%)`,
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(70% 90% at 8% 90%, ${tokens.red}26 0%, transparent 60%)`,
                    }}
                />

                {/* Content */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 1280,
                        mx: 'auto',
                        px: { xs: 3, md: 8 },
                        pb: { xs: 10, md: 12 },
                    }}
                >
                    {/* ONE Championship badge */}
                    <Box
                        onClick={() => document.getElementById('oneChampionshipSection')?.scrollIntoView({ behavior: 'smooth' })}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1.25,
                            cursor: 'pointer',
                            border: `1px solid ${tokens.red}`,
                            backgroundColor: 'rgba(224, 26, 34, 0.12)',
                            backdropFilter: 'blur(6px)',
                            px: 2,
                            py: 0.9,
                            mb: 3,
                            transition: `all 0.4s ${tokens.ease}`,
                            ':hover': { backgroundColor: 'rgba(224, 26, 34, 0.24)' },
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: tokens.red,
                                animation: 'vgPulse 1.8s ease-in-out infinite',
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                fontFamily: tokens.heading,
                                fontWeight: 600,
                                fontSize: { xs: 11, md: 13 },
                                letterSpacing: '0.24em',
                                textTransform: 'uppercase',
                                color: tokens.white,
                            }}
                        >
                            Siamo a ONE Championship
                        </Box>
                    </Box>

                    {/* Headline */}
                    <Box
                        component="h1"
                        sx={{
                            m: 0,
                            fontFamily: tokens.display,
                            fontSize: { xs: 62, sm: 104, md: 160 },
                            lineHeight: 0.86,
                            letterSpacing: '0.01em',
                            color: tokens.white,
                            textShadow: '0 20px 60px rgba(0,0,0,0.55)',
                        }}
                    >
                        TEAM
                        <br />
                        VIVENZIO
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: { xs: 1.5, md: 2.5 },
                            mt: 2.5,
                        }}
                    >
                        {['Muay Thai', 'Boxing', 'Saviano — Napoli'].map((item, index) => (
                            <React.Fragment key={item}>
                                {index > 0 && (
                                    <Box aria-hidden sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: tokens.red }} />
                                )}
                                <Box
                                    component="span"
                                    sx={{
                                        fontFamily: tokens.heading,
                                        fontSize: { xs: 13, md: 16 },
                                        letterSpacing: '0.22em',
                                        textTransform: 'uppercase',
                                        color: tokens.muted,
                                    }}
                                >
                                    {item}
                                </Box>
                            </React.Fragment>
                        ))}
                    </Box>

                    <BodyText textAlign="left" maxWidth={560} sx={{ mt: 3 }}>
                        L'arte degli otto arti, insegnata come si deve. Principianti e professionisti,
                        stessa sala, stessa disciplina.
                    </BodyText>

                    {/* CTAs */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                        <Button
                            variant="contained"
                            onClick={scrollToContatti}
                            sx={{
                                backgroundColor: tokens.red,
                                color: '#fff',
                                px: { xs: 3, md: 4 },
                                py: 1.6,
                                fontSize: isScreenSmall ? 13 : 15,
                                transition: `all 0.4s ${tokens.ease}`,
                                ':hover': {
                                    backgroundColor: tokens.redDeep,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 14px 36px ${tokens.red}55`,
                                },
                            }}
                        >
                            Allenati con noi
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={scrollToChiSiamo}
                            sx={{
                                color: tokens.white,
                                borderColor: tokens.lineStrong,
                                px: { xs: 3, md: 4 },
                                py: 1.6,
                                fontSize: isScreenSmall ? 13 : 15,
                                backdropFilter: 'blur(6px)',
                                transition: `all 0.4s ${tokens.ease}`,
                                ':hover': {
                                    borderColor: tokens.white,
                                    backgroundColor: 'rgba(255,255,255,0.06)',
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            Esplora
                        </Button>
                    </Box>
                    {/* Main sponsor credit — bottom-right on desktop so it
                        balances the CTAs, in flow under them on phones. */}
                    <Box
                        sx={{
                            position: { md: 'absolute' },
                            right: { md: 0 },
                            bottom: { md: 96 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.75,
                            mt: { xs: 4, md: 0 },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontFamily: tokens.heading,
                                fontSize: { xs: 10, md: 11 },
                                letterSpacing: '0.24em',
                                textTransform: 'uppercase',
                                color: tokens.muted,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Sponsorizzato da
                        </Box>
                        <Link
                            href="https://condominioforyou.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Condominio For You"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '4px',
                                px: 1.5,
                                py: 0.9,
                                transition: `transform 0.4s ${tokens.ease}`,
                                ':hover': { transform: 'translateY(-2px)' },
                            }}
                        >
                            <Box
                                component="img"
                                src={c4uLogo}
                                alt="Condominio For You"
                                sx={{ height: { xs: 16, md: 20 }, width: 'auto', display: 'block' }}
                            />
                        </Link>
                    </Box>

                </Box>

                {/* Scroll cue */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        bottom: 18,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: { xs: 'none', md: 'block' },
                        color: tokens.dim,
                        animation: 'vgScrollCue 2.2s ease-in-out infinite',
                    }}
                >
                    <KeyboardArrowDownIcon />
                </Box>
            </Box>

            {/* ==========================================================
                SECTIONS
            ========================================================== */}
            <OneChampionship />
            <ChiSiamo />
            <Activities />
            <RingSide />
            <SponsorPage />
            <Footer />
        </Box>
    );
}
